import { useRef, useState } from 'react'
import styled from 'styled-components'
import { useQueryClient } from '@tanstack/react-query'

import useApi from '../../hooks/useApi'
import useTranslation from '../../hooks/useTranslation'
import invalidateListQueries from '../../utils/invalidateListQueries'
import { icon } from '../../styles/icons'

/*
 * A list that takes files: drop them (or pick them) above the rows, and each
 * one is posted to `config.endpoint`, which files it into the list. Purchases ›
 * Inbox is the first (task 38.04): every receipt dropped becomes an entry.
 *
 * `listViewData.<list>.dropZone`:
 *   endpoint      where each file is posted (multipart, field `file`)
 *   label         the prompt in the zone (a translation key)
 *   accept        the file types the picker offers
 *
 * Files go one at a time, so a batch never floods the server and each one's
 * result is known; the list refreshes after every file.
 */

const Zone = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 7.5rem;
  margin-bottom: 1rem;
  padding: 1.25rem;
  border: 2px dashed ${({ theme, $dragging }) => $dragging ? theme.tab.navLinkColor : theme.form.border};
  border-radius: 1rem;
  background: ${({ theme }) => theme.form.background};
  color: ${({ theme }) => theme.font.label};
  text-align: center;
  cursor: pointer;
  transition: border-color .15s ease-in-out;

  svg { font-size: 2rem; }
  &:hover { border-color: ${({ theme }) => theme.form.activeBorder}; }
`

const Results = styled.ul`
  margin: -0.5rem 0 1rem;
  padding: 0;
  list-style: none;
  font-size: var(--font-XS);
`

const Result = styled.li`
  display: flex;
  gap: 0.5rem;
  padding: 0.125rem 0;
  color: ${({ theme, $tone }) => $tone === 'error' ? theme.emotive.error.color : theme.font.label};

  & > span:first-child {
    color: ${({ theme }) => theme.font.color};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

const ListDropZone = ({ config, listEndpoint, testId }) => {
  const { postMeForm } = useApi()
  const { t, m } = useTranslation()
  const queryClient = useQueryClient()
  const inputRef = useRef(null)

  const [dragging, setDragging] = useState(false)
  const [busy, setBusy] = useState(false)
  // One line per file of the last batch: { name, status: pending|done|duplicate|error, error? }
  const [results, setResults] = useState([])

  const setResult = (index, patch) => setResults(rs => rs.map((r, i) => (i === index ? { ...r, ...patch } : r)))

  const upload = async (files) => {
    const list = Array.from(files || [])
    if (!list.length || busy) return
    setBusy(true)
    setResults(list.map(f => ({ name: f.name, status: 'pending' })))
    for (const [i, file] of list.entries()) {
      const response = await postMeForm(config.endpoint, file, {})
      if (response?.data?.data) {
        setResult(i, { status: response.data.created === false ? 'duplicate' : 'done' })
        await invalidateListQueries(listEndpoint, queryClient)
      } else {
        setResult(i, { status: 'error', error: response?.error })
      }
    }
    setBusy(false)
  }

  const statusText = (r) => {
    if (r.status === 'pending') return t('all.dropZone.uploading')
    if (r.status === 'done') return t('all.dropZone.done')
    if (r.status === 'duplicate') return t('all.dropZone.duplicate')
    return r.error === 'file.upload.tooLarge' ? t('all.dropZone.tooLarge') : t('all.dropZone.failed')
  }

  const done = results.filter(r => r.status !== 'pending').length
  return (
    <>
      <Zone
        $dragging={dragging}
        data-testid={testId}
        onClick={() => !busy && inputRef.current?.click()}
        onDragOver={e => { e.preventDefault(); setDragging(true) }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => { e.preventDefault(); setDragging(false); upload(e.dataTransfer.files) }}
      >
        {icon.upload}
        <span>{busy ? m('all.dropZone.progress', [done + 1, results.length]) : t(config.label)}</span>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={config.accept}
          style={{ display: 'none' }}
          onChange={e => { upload(e.target.files); e.target.value = '' }}
        />
      </Zone>
      {results.length > 0 && (
        <Results>
          {results.map((r, i) => (
            <Result key={i} $tone={r.status === 'error' ? 'error' : null}>
              <span>{r.name}</span>
              <span>{statusText(r)}</span>
            </Result>
          ))}
        </Results>
      )}
    </>
  )
}

export default ListDropZone
